import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useSession } from '../context/SessionContext';
import { useProfile } from '../context/ProfileContext';
import { hydrateSession } from '../services/hydrationService';
import { logWorkoutSession } from '../services/logsService';
import { createRoutine, updateRoutine, deleteRoutine } from '../services/routinesService';
import { isCardioExercise } from '../constants/exerciseTaxonomy';
import IconButton from './IconButton';
import CategoryPanel from './CategoryPanel';
import Modal from './Modal';
import ActiveMission from './ActiveMission';
import ExerciseSelectorModal from './ExerciseSelectorModal';
import ExerciseHistoryModal from './ExerciseHistoryModal';
import './comic.css';

// tourLockedProfileId: set by App.js while GuidedTour.jsx is waiting for the
// real user to tap Discard on a specific Hero's Cancel-Mission dialog --
// passed straight through to that one profile's ActiveMission so it can
// disable its own "Keep Going" button for the duration (see ActiveMission.
// jsx's lockCancelConfirm prop). null/undefined every time no tour step is
// currently waiting on this.
const RoutinePicker = ({ userId, tourLockedProfileId = null }) => {
  const [routines, setRoutines] = useState([]);
  const { startSession, sessionsByProfile } = useSession();
  const { activeProfile } = useProfile();
  // Which profile's (if any) mission is currently showing in this tab --
  // the currently-focused Hero's own session, not any other Hero's.
  const activeProfileSession = activeProfile ? sessionsByProfile[activeProfile.id] : null;

  // Inline "Add Routine" affordance state. Mirrors the "Add Exercise" form in
  // WorkoutLog.jsx: an expand/collapse comic-panel form. The new routine joins
  // the owner-scoped list below automatically via the routines onSnapshot
  // listener, so no manual refresh is needed on success.
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [addingRoutine, setAddingRoutine] = useState(false);
  const [newRoutineName, setNewRoutineName] = useState("");
  // Ordered list of selected exercise ids. Selection order IS the training
  // order — first tapped is order 0, and so on.
  const [selectedIds, setSelectedIds] = useState([]);
  // How many sets are planned per exercise, keyed by exerciseId. Drives
  // ActiveMission's pre-rendered set table for that exercise. Defaults to 3
  // (a common convention, and what ActiveMission itself falls back to for
  // older routines saved before this field existed).
  const [setsById, setSetsById] = useState({});
  const DEFAULT_SETS = 3;
  const [savingRoutine, setSavingRoutine] = useState(false);
  const [routineError, setRoutineError] = useState(null);
  // Non-null while editing an existing routine (holds its id); null in
  // "add new" mode. The form/state above is shared between both modes.
  const [editingRoutineId, setEditingRoutineId] = useState(null);
  // Delete-a-routine confirmation: which routine's "are you sure?" popup is
  // open (id, or null). Destructive and unrecoverable, so it always goes
  // through an explicit confirm step rather than deleting on tap, same
  // convention as Cancel Mission / merge-an-exercise elsewhere in the app.
  const [deletingRoutineId, setDeletingRoutineId] = useState(null);
  const [deletingRoutine, setDeletingRoutine] = useState(false);
  const [deleteRoutineError, setDeleteRoutineError] = useState(null);
  // Which target-muscle sections are expanded in "Tap to add exercises",
  // keyed by muscle name. Defaults to all collapsed — matches the same
  // default-collapsed convention used for WorkoutLog.jsx's history panels.
  const [openMuscleGroups, setOpenMuscleGroups] = useState(() => new Set());
  // Search filter for "Tap to add exercises" -- when non-empty, a flat
  // name-matched list replaces the muscle-group/main-muscle nested browse
  // view entirely (searching and browsing-by-group are mutually exclusive
  // presentations of the same underlying library, same as ExercisePicker's
  // combobox elsewhere).
  const [exerciseSearch, setExerciseSearch] = useState("");
  // Opens the same search/browse/create-new popup used in Quick Start and
  // Manage My Exercises. Picking an existing exercise or creating a new one
  // both route through the existing toggleExercise (a true add-or-remove
  // toggle) -- a freshly-created exercise can't already be in selectedIds,
  // so this always adds it.
  const [showCreateExercise, setShowCreateExercise] = useState(false);
  // Which exercise's "i" history popup is open: { exerciseId, name } or null.
  const [historyExercise, setHistoryExercise] = useState(null);

  const toggleMuscleGroup = (muscle) => {
    setOpenMuscleGroups((prev) => {
      const next = new Set(prev);
      if (next.has(muscle)) next.delete(muscle);
      else next.add(muscle);
      return next;
    });
  };

  useEffect(() => {
    if (!userId) return;

    const q = query(collection(db, "routines"), where("ownerUserId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRoutines(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [userId]);

  // Fetch Exercise Library — merged two-tier fetch, same pattern WorkoutLog.jsx
  // uses. The library is split between official/curated docs (legacy
  // userId == "global") and this account's own custom exercises
  // (ownerUserId == the Manager's uid). Rules scope reads per-branch, so we run
  // TWO listeners (one per query shape) and merge into a single map, deduped by
  // doc id, so newly-added exercises (official or custom) show up here without
  // a refresh too.
  useEffect(() => {
    if (!userId) return undefined;

    const merged = new Map();
    const publish = () => setExerciseLibrary(Array.from(merged.values()));

    const applySnapshot = (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") merged.delete(change.doc.id);
        else merged.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
      });
      publish();
    };

    const officialUnsub = onSnapshot(
      query(collection(db, "exercises"), where("userId", "==", "global")),
      applySnapshot
    );
    const customUnsub = onSnapshot(
      query(collection(db, "exercises"), where("ownerUserId", "==", userId)),
      applySnapshot
    );

    return () => { officialUnsub(); customUnsub(); };
  }, [userId]);

  const handleSelect = async (routine) => {
    // Defensive: ProfileGate blocks the app until a profile exists, so this
    // should never be null here — but never fall back to writing an "N/A" log.
    if (!activeProfile) {
      alert("No active Hero selected. Pick a profile before starting a mission.");
      return;
    }

    try {
      const hydrated = await hydrateSession(routine);
      startSession(activeProfile.id, hydrated);

      await logWorkoutSession({
        exerciseId: "SESSION_START",
        profileId: activeProfile.id,
        ownerUserId: userId,
        weight: 0,
        reps: 0,
        note: `Started routine: ${routine.name}`
      });
    } catch (error) {
      console.error("Mission data corruption:", error);
      alert("Failed to decrypt mission.");
    }
  };

  // Toggle an exercise in/out of the ordered selection. Tapping an unselected
  // exercise appends it (becoming the next order); tapping a selected one
  // removes it, and everything after re-numbers automatically since order is
  // derived from array position at submit time.
  const toggleExercise = (exerciseId) => {
    setSelectedIds((prev) =>
      prev.includes(exerciseId)
        ? prev.filter((id) => id !== exerciseId)
        : [...prev, exerciseId]
    );
    setSetsById((prev) => {
      if (prev[exerciseId] !== undefined) {
        // Removing: drop its sets count too so re-adding it later starts fresh.
        const { [exerciseId]: _omit, ...rest } = prev;
        return rest;
      }
      // Cardio exercises are always exactly 1 set, automatically -- not a
      // default the stepper lets the user change (it's hidden for these,
      // see the "Mission order" row render below).
      return { ...prev, [exerciseId]: isCardioExerciseId(exerciseId) ? 1 : DEFAULT_SETS };
    });
  };

  const setSetsForExercise = (exerciseId, sets) => {
    // Clamp to a sane range — 0 sets makes no sense, and there's no reason
    // to support absurdly large values from a stray keystroke.
    const clamped = Math.max(1, Math.min(20, sets));
    setSetsById((prev) => ({ ...prev, [exerciseId]: clamped }));
  };

  // Press-and-drag reordering for the "Mission order" list, via the native
  // Pointer Events API (unifies mouse/touch/pen — no drag-and-drop library
  // needed). dragIndex is the row being lifted; dragOverIndex is whichever
  // row the pointer is currently over (the drop target if released now);
  // dragOffsetY drives the lifted row's live-follow transform.
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const dragStartY = useRef(0);
  const rowRefs = useRef([]);

  const handleDragPointerDown = (e, index) => {
    // Only the primary button/touch/pen contact starts a drag.
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartY.current = e.clientY;
    setDragIndex(index);
    setDragOverIndex(index);
    setDragOffsetY(0);
  };

  const handleDragPointerMove = (e) => {
    if (dragIndex === null) return;
    setDragOffsetY(e.clientY - dragStartY.current);

    // Find which row the pointer is currently over, by comparing against
    // each row's live bounding box (not just index math), so the drop
    // target tracks accurately regardless of row height/wrapping. Skip the
    // dragged row itself — its own bounding box follows the pointer via the
    // translateY transform below, so it would always "win" the hit-test
    // against itself before the loop ever reached the real target row.
    for (let i = 0; i < selectedIds.length; i++) {
      if (i === dragIndex) continue;
      const el = rowRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        if (i !== dragOverIndex) setDragOverIndex(i);
        break;
      }
    }
  };

  const endDrag = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragOverIndex !== dragIndex) {
      setSelectedIds((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(dragOverIndex, 0, moved);
        return next;
      });
    }
    setDragIndex(null);
    setDragOverIndex(null);
    setDragOffsetY(0);
  };

  // Group the exercise library by target muscle (category), then by main
  // muscle (sub-category) within each, for the "Tap to add exercises"
  // checklist — two levels of nesting, matching the two-tier exercise
  // schema. Preserves first-appearance order at both levels; exercises with
  // no targetMuscle/mainMuscle fall into an "Other" bucket at that level.
  const exercisesByMuscle = useMemo(() => {
    const groups = new Map();
    exerciseLibrary.forEach((ex) => {
      const muscle = ex.targetMuscle || "Other";
      if (!groups.has(muscle)) groups.set(muscle, new Map());
      const subGroups = groups.get(muscle);
      const main = ex.mainMuscle || "Other";
      if (!subGroups.has(main)) subGroups.set(main, []);
      subGroups.get(main).push(ex);
    });
    return Array.from(groups.entries()).map(([muscle, subGroups]) => ({
      muscle,
      list: Array.from(subGroups.values()).flat(),
      subGroups: Array.from(subGroups.entries()).map(([main, list]) => ({ main, list }))
    }));
  }, [exerciseLibrary]);

  // Falls back to a readable placeholder (not the raw Firestore doc id) when
  // an exerciseId in a routine's exercises array has no match in the
  // currently-loaded library — e.g. the exercise was deleted directly via
  // the Firebase console after the routine was built (there's no in-app
  // exercise-delete, so this can only happen from an out-of-band edit).
  const resolveExerciseName = (exerciseId) => {
    const match = exerciseLibrary.find((ex) => ex.id === exerciseId);
    return match ? match.name : "(deleted exercise)";
  };

  // A Cardio exercise's set count is always exactly 1 -- there's no "3 sets
  // of treadmill" the way there's "3 sets of bench press" -- so the
  // "Mission order" sets stepper below is hidden entirely for one, per
  // direct user request. A deleted-exercise id (no library match) falls
  // through as non-cardio, same as resolveExerciseName's own fallback.
  const isCardioExerciseId = (exerciseId) =>
    isCardioExercise(exerciseLibrary.find((ex) => ex.id === exerciseId));

  // Shared row for "Tap to add exercises" (used by both the search-results
  // list and the muscle-group/main-muscle browse list below) -- the tap
  // target toggles selection, and a separate "i" icon beside it opens that
  // exercise's history popup. Kept as one helper so the two call sites can't
  // drift out of sync.
  const renderExerciseOption = (ex) => {
    const isSelected = selectedIds.includes(ex.id);
    return (
      <div key={ex.id} style={{ display: "flex", gap: "6px", alignItems: "stretch" }}>
        <button
          type="button"
          onClick={() => toggleExercise(ex.id)}
          className="comic-panel comic-panel--entry comic-tap"
          style={{
            flex: 1,
            minWidth: 0,
            color: "#232526",
            fontFamily: 'Anonymous Pro',
            textAlign: "left",
            cursor: "pointer",
            fontSize: "16px",
            borderColor: isSelected ? "#00a274" : "#232526"
          }}
        >
          {isSelected ? "✓ " : "+ "}{ex.name}
        </button>
        <IconButton
          icon="info"
          onClick={() => setHistoryExercise({ exerciseId: ex.id, name: ex.name })}
          color="#232526"
          label={`View history for ${ex.name}`}
        />
      </div>
    );
  };

  const resetAddRoutineForm = () => {
    setNewRoutineName("");
    setSelectedIds([]);
    setSetsById({});
    setRoutineError(null);
    setAddingRoutine(false);
    setEditingRoutineId(null);
    setOpenMuscleGroups(new Set());
    setExerciseSearch("");
    setShowCreateExercise(false);
  };

  // Open the form pre-filled with an existing routine's current name/exercise
  // order, in edit mode. exercises on the doc aren't guaranteed pre-sorted,
  // so order is derived explicitly here rather than assumed. Routines saved
  // before the per-exercise sets field existed fall back to DEFAULT_SETS.
  const startEditRoutine = (routine) => {
    setRoutineError(null);
    setNewRoutineName(routine.name || "");
    const sorted = [...(routine.exercises || [])].sort((a, b) => a.order - b.order);
    setSelectedIds(sorted.map((e) => e.exerciseId));
    setSetsById(
      // A Cardio exercise is forced to 1 here too (not just on fresh add),
      // so a routine saved before this rule existed self-corrects the next
      // time it's opened for edit, even if its stored `sets` is stale.
      Object.fromEntries(sorted.map((e) => [e.exerciseId, isCardioExerciseId(e.exerciseId) ? 1 : (e.sets || DEFAULT_SETS)]))
    );
    setEditingRoutineId(routine.id);
    setAddingRoutine(true);
  };

  // Deleting a routine only removes its definition -- logged history is
  // never touched (logs reference exerciseId, not routineId; routineName is
  // a plain string copied onto a log at the time it was set, not a live
  // reference), so past workouts under this routine's name stay exactly as
  // they were in "Previously...".
  const handleConfirmDeleteRoutine = async () => {
    if (!deletingRoutineId || deletingRoutine) return;
    setDeletingRoutine(true);
    setDeleteRoutineError(null);
    const result = await deleteRoutine(deletingRoutineId);
    setDeletingRoutine(false);
    if (result.success) {
      setDeletingRoutineId(null);
    } else {
      setDeleteRoutineError("Couldn't delete that routine. Try again.");
    }
  };

  const handleAddRoutine = async (e) => {
    e.preventDefault();
    const name = newRoutineName.trim();
    if (!name || selectedIds.length === 0 || savingRoutine) return;

    // Client-side duplicate guard against the already-loaded, owner-scoped
    // routines list. Match is case-insensitive and trimmed on both sides,
    // full-name (not substring). routines is already filtered to the current
    // user, so this only checks the Manager's own routines. Excludes the
    // routine currently being edited, so saving it under its own unchanged
    // name doesn't false-positive as a duplicate of itself.
    const isDuplicate = routines.some(
      (r) => r.id !== editingRoutineId && r.name?.trim().toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      setRoutineError(`A routine named "${name}" already exists.`);
      return;
    }

    setSavingRoutine(true);
    setRoutineError(null);

    // Selection order IS the training order. Cardio is clamped to 1 set
    // here too (belt-and-suspenders on top of the stepper already being
    // hidden/forced for it everywhere above).
    const exercises = selectedIds.map((exerciseId, index) => ({
      exerciseId,
      order: index,
      sets: isCardioExerciseId(exerciseId) ? 1 : (setsById[exerciseId] || DEFAULT_SETS)
    }));

    const result = editingRoutineId
      ? await updateRoutine(editingRoutineId, name, exercises)
      : await createRoutine(userId, name, exercises);
    if (result.success) {
      // The routines onSnapshot listener reflects the create/update in the
      // list below automatically, so just collapse and clear the form.
      resetAddRoutineForm();
    } else {
      setRoutineError(`Couldn't ${editingRoutineId ? "update" : "save"} that routine. Try again.`);
    }
    setSavingRoutine(false);
  };

  const canSubmitRoutine = newRoutineName.trim().length > 0 && selectedIds.length > 0;

  return (
    <CategoryPanel
      mascotVariant="routines"
      outerClassName="comic-panel--category comic-panel--border-peach comic-panel--no-top-margin"
      title="Select Mission"
    >
      {/* One ActiveMission per profile with a live session, mounted
          permanently (same "stay mounted, hide via CSS" pattern the tab bar
          itself uses) and hidden unless it's the currently-focused Hero's --
          so switching focus to a different Hero via ProfileSwitcher/
          ActiveSessionsBar never unmounts (and never loses the in-progress,
          not-yet-logged state of) whichever mission you switch away from. */}
      {Object.keys(sessionsByProfile).map((pid) => (
        <div key={pid} style={{ display: activeProfile?.id === pid ? "block" : "none" }}>
          <ActiveMission userId={userId} profileId={pid} exerciseLibrary={exerciseLibrary} lockCancelConfirm={tourLockedProfileId === pid} />
        </div>
      ))}
      {activeProfileSession ? null : (
      <>
      {routines.map((routine) => (
        <div key={routine.id} style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
          <button
            onClick={() => handleSelect(routine)}
            className="comic-panel comic-panel--entry comic-tap"
            style={{
              flex: 1,
              minWidth: 0,
              color: "#232526",
              fontFamily: 'Anonymous Pro',
              textAlign: "left",
              cursor: "pointer",
              fontSize: "16px"
            }}
          >
            {routine.name}
          </button>
          <IconButton
            icon="edit"
            onClick={() => startEditRoutine(routine)}
            color="#232526"
            label={`Edit ${routine.name}`}
          />
          <IconButton
            icon="delete"
            onClick={() => { setDeleteRoutineError(null); setDeletingRoutineId(routine.id); }}
            color="#232526"
            label={`Delete ${routine.name}`}
          />
        </div>
      ))}
      {routines.length === 0 && (
        <p className="comic-empty">No active mission profiles found.</p>
      )}

      {deletingRoutineId && (() => {
        const routine = routines.find((r) => r.id === deletingRoutineId);
        return (
          <Modal title="Delete Routine?" tabKey="routines" onClose={() => setDeletingRoutineId(null)}>
            <p style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "14px", color: "#232526", margin: "0 0 14px 0" }}>
              Delete "{routine?.name}"? This can't be undone. Sets you've already logged with this routine stay in "Previously..." either way.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="button"
                onClick={handleConfirmDeleteRoutine}
                disabled={deletingRoutine}
                className="comic-btn comic-btn--end"
                style={{ flex: 1 }}
              >
                {deletingRoutine ? "Deleting..." : "Delete"}
              </button>
              <button
                type="button"
                onClick={() => setDeletingRoutineId(null)}
                disabled={deletingRoutine}
                className="comic-btn comic-btn--done"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
            {deleteRoutineError && <div className="comic-alert-bubble">{deleteRoutineError}</div>}
          </Modal>
        );
      })()}

      {addingRoutine && (
        <Modal title={editingRoutineId ? "Edit Routine" : "New Routine"} tabKey="routines" onClose={resetAddRoutineForm}>
        <form onSubmit={handleAddRoutine}>
          <input
            type="text"
            placeholder="Routine name"
            value={newRoutineName}
            onChange={(e) => setNewRoutineName(e.target.value)}
            className="comic-input"
            style={{ width: "100%", marginBottom: "14px" }}
            autoFocus
          />

          <h3 className="comic-title" style={{ fontSize: "18px" }}>
            Tap to add exercises
          </h3>
          <button
            type="button"
            onClick={() => setShowCreateExercise(true)}
            className="comic-btn comic-btn--go"
            style={{ marginBottom: "10px" }}
          >
            + Create New Exercise
          </button>
          {showCreateExercise && (
            <ExerciseSelectorModal
              exercises={exerciseLibrary}
              userId={userId}
              tabKey="routines"
              title="Add Exercise"
              onSelect={(ex) => toggleExercise(ex.id)}
              onClose={() => setShowCreateExercise(false)}
            />
          )}
          {exerciseLibrary.length > 0 && (
            <input
              type="text"
              value={exerciseSearch}
              onChange={(e) => setExerciseSearch(e.target.value)}
              placeholder="Select or Type to Search"
              className="comic-input"
              style={{ width: "100%", marginBottom: "10px" }}
            />
          )}
          {exerciseLibrary.length === 0 ? (
            <p className="comic-empty">No exercises in the library yet.</p>
          ) : exerciseSearch.trim() ? (
            (() => {
              const matches = exerciseLibrary.filter((ex) =>
                ex.name.toLowerCase().includes(exerciseSearch.trim().toLowerCase())
              );
              return matches.length === 0 ? (
                <p className="comic-empty">No exercises match "{exerciseSearch.trim()}".</p>
              ) : (
                matches.map((ex) => renderExerciseOption(ex))
              );
            })()
          ) : (
            exercisesByMuscle.map(({ muscle, list, subGroups }) => {
              const isOpen = openMuscleGroups.has(muscle);
              // Count of this muscle's exercises already in the selection, so
              // a collapsed section still shows at a glance whether anything
              // inside it is picked.
              const selectedCount = list.filter((ex) => selectedIds.includes(ex.id)).length;
              return (
                <div key={muscle}>
                  <button
                    type="button"
                    onClick={() => toggleMuscleGroup(muscle)}
                    aria-expanded={isOpen}
                    className="comic-eyebrow"
                    style={{ marginTop: "10px", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  >
                    {muscle}{selectedCount > 0 ? ` (${selectedCount})` : ""}
                    <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
                  </button>
                  {isOpen && subGroups.map(({ main, list: subList }) => (
                    <div key={main}>
                      <div
                        style={{
                          fontFamily: 'Anonymous Pro', fontSize: "12px", fontWeight: "bold",
                          color: "#232526", opacity: 0.65, margin: "8px 0 4px 4px", textTransform: "uppercase"
                        }}
                      >
                        {main}
                      </div>
                      {subList.map((ex) => renderExerciseOption(ex))}
                    </div>
                  ))}
                </div>
              );
            })
          )}

          {selectedIds.length > 0 && (
            <>
              <h3 className="comic-title" style={{ fontSize: "18px", marginTop: "14px" }}>
                Mission order
              </h3>
              {selectedIds.map((id, index) => {
                const isDragging = dragIndex === index;
                const isDropTarget = dragIndex !== null && dragOverIndex === index && !isDragging;
                return (
                  <div
                    key={id}
                    ref={(el) => (rowRefs.current[index] = el)}
                    className="stamp-row"
                    style={{
                      position: "relative",
                      touchAction: isDragging ? "none" : undefined,
                      transform: isDragging ? `translateY(${dragOffsetY}px)` : undefined,
                      zIndex: isDragging ? 10 : undefined,
                      background: isDragging ? "#fff8ea" : undefined,
                      boxShadow: isDragging ? "3px 3px 0 0 #232526" : undefined,
                      borderTop: isDropTarget ? "2px solid #00a274" : undefined
                    }}
                  >
                    <span
                      onPointerDown={(e) => handleDragPointerDown(e, index)}
                      onPointerMove={handleDragPointerMove}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                      aria-label={`Drag to reorder ${resolveExerciseName(id)}`}
                      role="button"
                      style={{
                        display: "inline-grid",
                        gridTemplateColumns: "repeat(2, 4px)",
                        gridTemplateRows: "repeat(3, 4px)",
                        gap: "3px",
                        padding: "8px 6px",
                        margin: "-8px -6px 0 0",
                        touchAction: "none",
                        cursor: isDragging ? "grabbing" : "grab"
                      }}
                    >
                      {Array.from({ length: 6 }).map((_, dotIndex) => (
                        <span
                          key={dotIndex}
                          aria-hidden="true"
                          style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#232526" }}
                        />
                      ))}
                    </span>
                    <span className="stamp-row__badge">{index + 1}</span>
                    <span className="stamp-row__text">{resolveExerciseName(id)}</span>
                    {isCardioExerciseId(id) ? (
                      // No stepper -- a Cardio exercise's set count is
                      // always exactly 1, automatically, not user-adjustable.
                      <span style={{ fontFamily: 'Anonymous Pro', fontSize: "13px", minWidth: "42px", textAlign: "center" }}>
                        1 set
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        <button
                          type="button"
                          onClick={() => setSetsForExercise(id, (setsById[id] || DEFAULT_SETS) - 1)}
                          aria-label={`Fewer sets for ${resolveExerciseName(id)}`}
                          style={{
                            width: "22px", height: "22px", background: "none",
                            border: "1px solid #232526", borderRadius: "50%",
                            fontSize: "13px", lineHeight: 1, cursor: "pointer", color: "#232526"
                          }}
                        >
                          −
                        </button>
                        <span style={{ fontFamily: 'Anonymous Pro', fontSize: "13px", minWidth: "42px", textAlign: "center" }}>
                          {setsById[id] || DEFAULT_SETS} sets
                        </span>
                        <button
                          type="button"
                          onClick={() => setSetsForExercise(id, (setsById[id] || DEFAULT_SETS) + 1)}
                          aria-label={`More sets for ${resolveExerciseName(id)}`}
                          style={{
                            width: "22px", height: "22px", background: "none",
                            border: "1px solid #232526", borderRadius: "50%",
                            fontSize: "13px", lineHeight: 1, cursor: "pointer", color: "#232526"
                          }}
                        >
                          +
                        </button>
                      </span>
                    )}
                    <IconButton
                      icon="info"
                      onClick={() => setHistoryExercise({ exerciseId: id, name: resolveExerciseName(id) })}
                      color="#232526"
                      label={`View history for ${resolveExerciseName(id)}`}
                    />
                    <IconButton
                      icon="close"
                      onClick={() => toggleExercise(id)}
                      color="#fd874e"
                      label={`Remove ${resolveExerciseName(id)} from routine`}
                    />
                  </div>
                );
              })}
            </>
          )}

          <button
            type="submit"
            disabled={savingRoutine || !canSubmitRoutine}
            className="comic-btn comic-btn--go"
          >
            {savingRoutine ? "Saving..." : editingRoutineId ? "Update Routine" : "Save Routine"}
          </button>
          <button
            type="button"
            onClick={resetAddRoutineForm}
            className="comic-btn comic-btn--end"
          >
            Cancel
          </button>
          {routineError && <div className="comic-alert-bubble">{routineError}</div>}
        </form>
        </Modal>
      )}

      {historyExercise && (
        <ExerciseHistoryModal
          exerciseId={historyExercise.exerciseId}
          exerciseName={historyExercise.name}
          userId={userId}
          tabKey="routines"
          onClose={() => setHistoryExercise(null)}
        />
      )}

      <button
        type="button"
        onClick={() => { setRoutineError(null); setAddingRoutine(true); }}
        className="comic-btn comic-btn--go"
      >
        + Add Routine
      </button>
      </>
      )}
    </CategoryPanel>
  );
};

export default RoutinePicker;
